import { useAnalytics } from "@lazarentertainment/analytics-collector"
import { useChannelContext } from "client/components/channel"
import {
  ImageFragment,
  ProductFragment,
  ProductOptionsFragment,
  ProductVariantFragment,
  useGetProductByIdQuery,
} from "client/graphql/commerce-v2-api"
import { useAnimationContext } from "client/packages/animations/AnimationContext"
import { OverlaidAnimationStyle } from "client/packages/animations/types"
import {
  NumberInput,
  SelectMenu,
} from "client/packages/commerce/components/ui/inputs"
import { CommerceFooter } from "client/packages/commerce/components/ui/styles/CommerceFooters"
import { ScrollWrapper } from "client/packages/commerce/components/ui/styles/Containers"
import {
  Form,
  ProductSelectionInputGroup,
} from "client/packages/commerce/components/ui/styles/FormElementStyles"
import _ from "lodash"
import {
  Dispatch,
  Reducer,
  SetStateAction,
  useEffect,
  useMemo,
  useReducer,
} from "react"
import { FieldValues, useForm } from "react-hook-form"
import { FormattedMessage } from "react-intl"
import { AccentButton } from "shared/components/buttons/AccentButton"
import BackButton from "shared/components/buttons/BackButton"
import { useComponentId } from "shared/hooks/useComponentId"
import styled from "styled-components"

import {
  MAX_QUANTITY,
  TargetCartItem,
  useTargetCartContext,
} from "./cart/TargetCartContext"
import { useTargetContext } from "./TargetContext"
import ImageGallery from "./TargetImageGallery"
import ProductPreview from "./TargetProductPreview"

const TargetForm = styled(Form)`
  * {
    font-family: "Target", Helvetica, sans-serif !important;
  }
`

// Retrieves a variant based on the selected options. The assumption here
// is that the productOptions fully specify up to ONE variant. That is, if
// a product has 3 options, then there can only be one variant per unique
// combination of option values.
function getVariantForOptions(
  variants: ProductVariantFragment[] | null,
  productOptions: FieldValues,
): ProductVariantFragment | null {
  if (!variants) {
    return null
  }

  return (
    variants.find((variant) => {
      return variant.options.every((option) => {
        return option.value === productOptions[option.name]
      })
    }) ?? null
  )
}

// Retrieves all the variants that match some option values. This is different
// than the function above because it allows for passing a partial list of
// option values. If a product has 3 options, but you pass only 2 option values
// then you can have any number of variants that match those two option values.
function getAllVariantsForOptions(
  variants: ProductVariantFragment[] | null,
  productOptions: FieldValues,
): ProductVariantFragment[] {
  if (!variants) {
    return []
  }

  return variants.filter((variant) => {
    const filteredOptions = variant.options.filter((option) => {
      return option.value === productOptions[option.name]
    })

    return filteredOptions.length === Object.keys(productOptions).length
  })
}

function getOptionsForVariant(
  variant?: ProductVariantFragment | null,
): Record<string, string> {
  return (
    variant?.options.reduce(
      (acc, { name, value }) => {
        acc[name] = value

        return acc
      },
      {} as Record<string, string>,
    ) ?? {}
  )
}

function getButtonText(selectedVariant?: ProductVariantFragment | null) {
  // This should only happen if a variant matching all default option values doesn't exist.
  if (!selectedVariant) {
    return (
      <FormattedMessage
        id="commerce.unavailable"
        defaultMessage="Unavailable"
      />
    )
  }

  if (!selectedVariant.available) {
    return (
      <FormattedMessage id="commerce.soldOut" defaultMessage="Sold Out" />
    )
  }

  return (
    <FormattedMessage id="commerce.addToCart" defaultMessage="Save item" />
  )
}

function isImageFragment(
  image?: ImageFragment | null,
): image is ImageFragment {
  return !!image
}

function getImagesFromVariant(
  variant?: ProductVariantFragment | null,
): ImageFragment[] {
  if (!variant) {
    return []
  }

  return [variant.featuredImage, ...variant.images].filter(isImageFragment)
}

type TargetProductSelectionState = {
  initialImageGalleryIndex: number
  product: ProductFragment | null
  selectedVariant: ProductVariantFragment | null
  defaultValues: Record<string, string>
  formValues: Record<string, string>

  // We are reflecting option into local state rather than directly display options from
  // the ProductFragment because we need to be able to filter the options that are
  // displayed to the user based on their selection
  options: ProductOptionsFragment[]
}

const targetProductSelectionDefaultState: TargetProductSelectionState = {
  initialImageGalleryIndex: 0,
  selectedVariant: null,
  product: null,
  defaultValues: {},
  formValues: {},
  options: [],
}

// Calculates a similarity score based on the number of matching options.
const calculateOptionSimilarity = (
  options: ProductVariantFragment["options"],
  desiredOptions: ProductVariantFragment["options"],
): number =>
  desiredOptions.reduce((prev, curr) => {
    const option = options.find(({ name }) => name === curr.name)
    if (option && option.value === curr.value) {
      return prev + 1
    }
    return prev
  }, 0)

// Finds the variant which is available and most similar to the provided Variant ID
// considering the featured image and option similarity.
const getClosestAvailableVariant = (
  variantId: string,
  variants: ProductVariantFragment[],
): ProductVariantFragment => {
  const variant = variants.find(({ id }) => id === variantId)

  if (variant?.available) {
    return variant
  }

  // Beware of a bug when importing `lodash/chain`:
  // https://github.com/vercel/next.js/issues/52188
  const sortedVariants = _.chain(variants)
    // Assign all available variants to one of two groups depending on whether
    // they share the same image as the desired variant.
    .reduce<Record<string, ProductVariantFragment[]>>(
      (acc, curr) => {
        if (curr.available) {
          if (curr.featuredImage?.url === variant.featuredImage?.url) {
            acc.variantsWithMatchingImage.push(curr)
          } else {
            acc.variantsWithoutMatchingImage.push(curr)
          }
        }

        return acc
      },
      {
        variantsWithMatchingImage: [],
        variantsWithoutMatchingImage: [],
      },
    )
    // Sort each group individually based on option similarty to the desired variant.
    .mapValues((variants) =>
      variants.sort(
        (a, b) =>
          calculateOptionSimilarity(b.options, variant.options) -
          calculateOptionSimilarity(a.options, variant.options),
      ),
    )
    .values()
    .flatten()
    .value()

  return sortedVariants[0] ?? variant
}

// Some variants have an unspecified relationship between them. For example, Size Categories
// narrow the list of available sizes to choose from. In other cases, variants might just
// not be available anymore. Therefore, as a user makes selections, other options will have
// to be filtered so that the options shown to the user are always coherent.
//
//
// If you have a form like this, when the Size Group changes, the Size and
// the Color options need to be updated (even if a user has already made a selection)
// Because not all Sizes are available in each Size Group and not all color may be
// available in the new Size Group and Size
//
// Size Group
// |_Regular_________________________| <- If this changes so Plus Size then the other selections don't apply anymore.
//
// Size
// |_Small___________________________|
//
// Color
// |_Plaid___________________________|
//
function filterOptions(
  changedOptionName: string,
  values: Record<string, string>,
  state: TargetProductSelectionState,
): TargetProductSelectionState {
  const { product, options } = state

  if (!product) {
    return state
  }
  // Copy the current form values so we know what is currently selected. If a selection
  // hasn't taken place yet then some of the selections might be undefined. In that case
  // assume that the first value for each option is the selection.
  //
  // After we find which option has changed though, we may need to update the form to make
  // new selections which is why we copy them to a new object that can be updated.
  // nextValues will hold those selections and be updated as filtering takes place.
  const nextValues = product.options.reduce(
    (acc, option) => {
      if (!acc[option.name]) {
        acc[option.name] = option.values[0]
      }

      return acc
    },
    { ...values },
  )

  // Similar to nextValues above, but for the specific options lists, rather
  // than the selected form values
  const newOptions: ProductOptionsFragment[] = []

  // Indicates whether we have reached the changed option as we are looping through
  // the list of options. Only options that are below the option that has changed
  // need to be re-filtered so until we hit the changed option, we can use
  // the existing values.
  let found = false

  for (let i = 0; i < product.options.length; i++) {
    const opt = product.options[i]
    if (found) {
      // The changed option was previously found so all subsequent options
      // need to be re-filtered so that we display only valid options to
      // the user.

      // The values that we are checking against are the all the values
      // that are selected higher up in the form. The reason we simply
      // can't use the actual selected values returned from getValues,
      // is that as options are filtered, it may change the selection
      // in the form. Therefore we always need to be filtering based on the
      // newOptions.
      const checkValues = newOptions.reduce(
        (acc, option) => {
          acc[option.name] = nextValues[option.name]

          return acc
        },
        {} as Record<string, string>,
      )

      const values = opt.values.filter((value) => {
        checkValues[opt.name] = value

        // There may be multiple variants for a given set of options because
        // we aren't necessarily exhausting the check values. For example,
        // If a product has three options, Size Group, Size, and Color, we
        // may be selecting a variant only based on the Size Group, in which
        // case we will get many variants back.
        const variants = getAllVariantsForOptions(
          product.variants,
          checkValues,
        )

        // As long as one of the variants is available, put the value in the
        // the list
        return variants.some((variant) => variant.available)
      })

      newOptions.push({
        ...opt,
        values,
      })

      if (values.length !== opt.values.length) {
        nextValues[opt.name] = values[0]
      }
    } else {
      // Either we haven't found the changed option or this opt in the changed option.
      // Either way push the existing option to the list of new options, since these
      // options don't need to be filtered
      newOptions.push(options[i] ?? opt)

      // If the current opt is the changed option then mark it as found. From
      // now on, all branches lead to the above branch.
      found = opt.name === changedOptionName
    }
  }

  // Choose which variant is the next selected one based on the change in form values
  const nextSelectedVariant =
    // Ideally we find an exact match for the values that the user has selected and
    // we display that to the user.
    getVariantForOptions(product?.variants ?? null, nextValues) ??
    // However in some cases a variant can't be found to match the user's selection.
    // In that case the user cannot buy this variant but we still need to display
    // *something*. Following what Target does on their website, pick the first
    // option from the newly changed selection values. Find the first variant that
    // matches that selection alone. When this is displayed to the user because it
    // does not match the user selection, it will be displayed as SOLD OUT
    getAllVariantsForOptions(product.variants, {
      [Object.keys(nextValues)[0]]: Object.values(nextValues)[0],
    })[0]

  return {
    ...state,
    selectedVariant: nextSelectedVariant,
    options: newOptions,
    formValues: nextValues,
  }
}

function createDefaultState(
  product?: ProductFragment | null,
  state = targetProductSelectionDefaultState,
): TargetProductSelectionState {
  const nextState: TargetProductSelectionState = { ...state }

  if (product) {
    const selectedVariant = getClosestAvailableVariant(
      product.preselectedVariant.id,
      product.variants,
    )
    const defaultValues = getOptionsForVariant(selectedVariant)

    nextState.defaultValues = defaultValues
    nextState.selectedVariant = selectedVariant
    nextState.product = product
    nextState.options = []

    if (product.options.length > 0) {
      // When the product changes, if the product has multiple options, do an
      // initial options filtering. This is especially important for handling
      // Size Groups. If we don't do this then the user will see all combinations
      // of options, including nonsensical ones like Size Group = Regular and
      // Size 14W
      const variantOptions = selectedVariant.options.reduce(
        (acc, { name, value }) => {
          acc[name] = value
          return acc
        },
        {},
      )

      return filterOptions(
        _.last(selectedVariant.options).name,
        variantOptions,
        nextState,
      )
    }
  }

  return nextState
}

type TargetProductSelectionAction =
  | {
      type: "OPEN_IMAGE_GALLERY"
      payload: {
        initialImageGalleryIndex: number
      }
    }
  | {
      type: "CLOSE_IMAGE_GALLERY"
    }
  | {
      type: "SET_PRODUCT"
      payload: {
        product: ProductFragment
      }
    }
  | {
      type: "USER_SELECTION"
      payload: {
        changedOptionName: string
        formValues: Record<string, string>
      }
    }

type TargetProductSelectionReducer = Reducer<
  TargetProductSelectionState,
  TargetProductSelectionAction
>

const targetProductSelectionReducer: TargetProductSelectionReducer = (
  state,
  action,
) => {
  switch (action.type) {
    case "OPEN_IMAGE_GALLERY":
      return {
        ...state,
        initialImageGalleryIndex: action.payload.initialImageGalleryIndex,
      }
    case "CLOSE_IMAGE_GALLERY":
      return {
        ...state,
        initialImageGalleryIndex: 0,
      }
    case "SET_PRODUCT": {
      return createDefaultState(action.payload.product, state)
    }
    case "USER_SELECTION":
      return filterOptions(
        action.payload.changedOptionName,
        action.payload.formValues,
        state,
      )
    default:
      return state
  }
}

export type ProductSelectionProps = {
  productId: string
  currencyCode: string
  onBack?: (() => void) | null
  showConfirmation: Dispatch<SetStateAction<TargetCartItem>>
}

const ProductSelection = ({
  productId,
  currencyCode,
  onBack,
  showConfirmation,
}: ProductSelectionProps) => {
  const { analytics } = useAnalytics()
  const { channelId, broadcastId, recordingId } = useChannelContext()
  const { addToCart } = useTargetCartContext()
  const { showImageGallery, setShowImageGallery } = useTargetContext()
  const { setOverlaidAnimation } = useAnimationContext()

  // We are reflecting option into local state rather than directly display options from
  // the ProductFragment because we need to be able to filter the options that are
  // displayed to the user based on their selection
  // const [options, setOptions] = useState<ProductOptionsFragment[]>([])

  // Fetch the product by ID. The first time around, this will hit the network.
  // Afterwards it should return immediately from the cache. Both cases needed
  // to be handled
  const { data, loading: isProductLoading } = useGetProductByIdQuery({
    variables: {
      id: productId,
    },
  })

  // This is the product that is actually returned from our query. We are going to
  // reflect this into our reducer to make sure that all state stays in sync
  const product = data?.instance?.activeSale?.product

  // This is our main component state. We use a reducer here because many state changes
  // have to happen in response to use selection or product changes. This is the most
  // efficient way to batch them all together.
  const [state, dispatch] = useReducer(
    targetProductSelectionReducer,
    product,
    createDefaultState,
  )

  const images = useMemo(
    () => getImagesFromVariant(state.selectedVariant),
    [state.selectedVariant],
  )

  // It's unclear if the formHook is giving us any utility. I think it's likely an odd fit
  // for this component because we need to manage so much state outside of the form and then
  // set it onto the form hook rather than just passing the state down directly in one step.
  // For now leave this in to avoid too many changes at once.
  const formHook = useForm({
    defaultValues: state.defaultValues,
  })
  const {
    formState: { errors },
    handleSubmit,
    register,
    getValues,
    setValue,
    watch,
  } = formHook
  const componentId = useComponentId()
  const formId = `target-product-selection-form-${componentId}`

  // If the product ever changes set the product so that the whole reducer state is reset
  useEffect(() => {
    if (product?.id) {
      dispatch({
        type: "SET_PRODUCT",
        payload: {
          product,
        },
      })
    }
  }, [product?.id])

  // This is subtly different than the effect above. In this case we are looking for state.product
  // changes. The reason to do this is that we want to ensure that all our component state has
  // resolved and is consistent prior to setting new values on the form. If we listen to product
  // changes directly then we can get into a case where we set form values based on old state.
  useEffect(() => {
    const variant = state.selectedVariant

    if (variant) {
      variant.options.forEach(({ name, value }) => {
        setValue(name, value)
      })
    }
  }, [state.product])

  // This is an awkward part of using formHook. We are storing form values using the reducer because
  // form values might change after selections that cause value filtering. In that case we need to
  // reflect the formValue changes back into the form. Ideally we would just use the reducer
  // state directly but for now we go through this intermediary.
  useEffect(() => {
    const nextValues = state.formValues
    // If there have been any options filtered then we will need to update the
    // form with new selections. This is a bit of a dicey prospect because setting
    // values can land us back in this function since we are watching all form
    // changes. Therefore we need to ensure that we are only setting values that
    // have actually changed. After one round of changes the filtering will be
    // in-sync with the actual for values and no more changes will be made.
    Object.keys(nextValues).forEach((name) => {
      const values = getValues()

      if (values[name] !== nextValues[name]) {
        setValue(name, nextValues[name], {
          shouldDirty: false,
          shouldTouch: false,
        })
      }
    })
  }, [state.formValues])

  // Any time a user makes a selection, set the USER_SELECTION so that we can filter options based
  // the selection that the user made.
  useEffect(() => {
    const subscription = watch((_, change) => {
      if (!change.name) {
        return
      }

      subscription.unsubscribe()

      dispatch({
        type: "USER_SELECTION",
        payload: {
          changedOptionName: change.name,
          formValues: getValues(),
        },
      })
    })

    return subscription.unsubscribe
  }, [watch, product, state.options])

  const onSubmit = (values: FieldValues) => {
    const { product, selectedVariant } = state

    if (!product || !selectedVariant) {
      return
    }

    setOverlaidAnimation(OverlaidAnimationStyle.HEARTS)

    const cartItem: TargetCartItem = {
      productId: product.id,
      variantId: selectedVariant.id,
      price: selectedVariant.price,
      quantity: parseInt(values.quantity, 10),
    }
    addToCart(cartItem)
    showConfirmation(cartItem)

    analytics.track("target_add_to_cart", {
      channelId,
      broadcastId,
      recordingId,
      productId: cartItem.productId,
      variantId: cartItem.variantId,
      price: cartItem.price,
      quantity: cartItem.quantity,
    })
  }

  if (isProductLoading || !state.product || !state.selectedVariant) {
    return null
  }

  if (showImageGallery) {
    return (
      <ImageGallery
        images={images}
        initialIndex={state.initialImageGalleryIndex}
        onBack={() => {
          dispatch({
            type: "CLOSE_IMAGE_GALLERY",
          })
          setShowImageGallery(false)
        }}
      />
    )
  }

  return (
    <>
      <ScrollWrapper>
        <ProductPreview
          currencyCode={currencyCode}
          product={state.product}
          images={images}
          price={
            state.selectedVariant
              ? state.selectedVariant.price
              : state.product.priceRange.minVariantPrice
          }
          openImageGallery={(initialImageGalleryIndex = 0) => {
            dispatch({
              type: "OPEN_IMAGE_GALLERY",
              payload: {
                initialImageGalleryIndex,
              },
            })
            setShowImageGallery(true)
          }}
        />
        <TargetForm onSubmit={handleSubmit(onSubmit)} id={formId}>
          <ProductSelectionInputGroup>
            {state.options.map((option, i) => {
              // If the options list has only one value then we simply pre-fill a hidden
              // input and don't bother giving the user an option to select. However, the
              // reason we are checking the full list of product options and not the filtered
              // options list is so that inputs don't appear/disappear during selection. For
              // example if a shirt comes in several colors and the user selects Size Small,
              // there may only be one Color option for that Size. We don't want the input
              // to disappear in that case and then reappear if the user selects Size Medium
              // that comes in several Color options.
              if (state.product?.options[i].values.length === 1) {
                return (
                  <input
                    type="hidden"
                    key={option.name}
                    {...register(option.name)}
                    defaultValue={option.values[0]}
                  />
                )
              }

              return (
                <SelectMenu
                  key={option.name}
                  id={`optionsmenu-${option.name}`}
                  name={option.name}
                  values={option.values}
                  handleSelect={setValue}
                  error={errors[option.name]}
                  label={option.name}
                  required
                  formHook={formHook}
                  defaultValue={option.values[0]}
                  disabled={option.values.length === 0}
                />
              )
            })}

            <NumberInput
              min={1}
              max={MAX_QUANTITY}
              name="quantity"
              defaultValue={1}
              label={
                <FormattedMessage
                  id="checkout.quantity"
                  defaultMessage="Qty"
                />
              }
              formHook={formHook}
              disabled={
                !!state.selectedVariant && !state.selectedVariant.available
              }
            />
          </ProductSelectionInputGroup>
        </TargetForm>
      </ScrollWrapper>
      <CommerceFooter>
        {onBack && <BackButton onClick={onBack} />}
        <AccentButton
          disabled={!state.selectedVariant?.available}
          type="submit"
          form={formId}
        >
          {getButtonText(state.selectedVariant)}
        </AccentButton>
      </CommerceFooter>
    </>
  )
}

export { ProductSelection }
